import React, { useState, useEffect } from 'react';
import { ProgressBar, Button, Card } from 'react-bootstrap';
import { Upload, FileText } from 'lucide-react';
import { Document, Page } from 'react-pdf';

import { pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;



const Resumescore = () => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [fileError, setFileError] = useState("");
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    document.body.style.background = 'linear-gradient(90deg, rgba(2,0,36,1) 0%, rgba(9,9,121,1) 35%, rgba(0,212,255,1) 100%);';
    return () => {
      document.body.style.background = ''; 
    };
  }, []);
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };
  const handleFile = (file) => {
    if (file && (file.type === 'application/pdf' || file.name.endsWith('.pdf'))) {
      if (file.size > 10 * 1024 * 1024) {
        setFileError('File size is too large. Please upload a file under 10MB.');
        return;
      }
      setFile(file);
      setFilePreview(URL.createObjectURL(file));
      setStep(2);
      setScoreData(null);
      setFileError("");
      setPageNumber(1);
      setPdfError(null);
    } else {
      setFileError('Please upload a valid PDF file.');
    }
  };
  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  };
  const onDocumentLoadError = (error) => {
    setPdfError('Error loading PDF file.');
    console.error(error);
  };
  const analyzeResume = async () => {
    if (!file) {
      setFileError('Please upload a file before analyzing.');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append('resumeFile', file);
    try {
      const response = await fetch('https://ai-powered-cv-maker-api.vercel.app/api/resume/analyze', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      console.log('API Response Data:', data);
      if (data && data.success && data.data) {
        const parseScore = (score) => {
          if (score && score.includes('/')) {
            return parseInt(score.split('/')[0]);
          }
          return 0;
        };
        setScoreData({
          success: true,
          error: false,
          message: data.message || 'Resume analyzed successfully',
          data: {
            overAllScore: parseScore(data.data.overAllScore),
            readabilityScore: parseScore(data.data.readabilityScore),
            keywordScore: parseScore(data.data.keywordScore),
            grammarScore: parseScore(data.data.grammarScore),
            suggestions: data.data.suggestions || [],
            readabilitySuggestions: data.data.readabilitySuggestions || [],
            grammarSuggestions: data.data.grammarSuggestions || [],
          },
        });
        setStep(3);
      } else {
        throw new Error('Invalid response data structure');
      }
    } catch (error) {
      console.error('Error during resume analysis:', error);
      setScoreData({
        success: false,
        error: true,
        message: `Failed to analyze resume: ${error.message}`,
        data: {},
      });
    } finally {
      setLoading(false);
    }
  };
  const renderStep1 = () => (
    <div
      className={`p-3 text-center ${dragActive ? 'bg-light' : 'bg-transparent'}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <Upload className="mb-2" size={48} />
      <h5>We can read: PDF</h5>
      <p>or</p>
      <h4>Drop to upload your resume or choose a file. Max 10MB file size.</h4>
      <label className="btn btn-primary">
        Choose File
        <input
          type="file"
          className="d-none"
          accept=".pdf"
          onChange={handleFileInput}
        />
      </label>
      {fileError && <div className="mt-3 text-danger">{fileError}</div>}
    </div>
  );  
  const renderPDFPreview = () => (
    <div className="pdf-preview-container" style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '', 
      overflowY: 'auto', 
      marginBottom: '20px' }}>
      {pdfError ? (
        <div className="text-danger">{pdfError}</div>
      ) : (
        <>
          <Document
            file={filePreview}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            className="border border-gray-300 shadow-lg"
          >
            <Page 
              pageNumber={pageNumber}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="max-w-full"
            />
          </Document>
          {numPages && (
            <div className="mt-4 d-flex justify-content-center align-items-center gap-3">
            </div>
          )}
        </>
      )}
    </div>
  );
  const renderStep2 = () => (
    <div className="text-center">
      <FileText className="mb-3" size={48} />
      <h5>{file ? file.name : 'No file selected'}</h5>
      {renderPDFPreview()}
      <Button
        variant="primary"
        onClick={analyzeResume}
        disabled={loading || !file}
      >
        {loading ? 'Analyzing...' : 'Analyze Resume'}
      </Button>
    </div>
  );
  const renderStep3 = () => (
    <>
      {renderPDFPreview()}
      <Card>
        <Card.Body>
          <Card.Title>Overall Score</Card.Title>
          <h2 className="text-center">{scoreData?.data?.overAllScore || '0'}</h2>
          <ProgressBar now={parseInt(scoreData?.data?.overAllScore || 0)} />
        </Card.Body>
      </Card>
      <div className="d-flex justify-content-between mt-4">
        <Card className="flex-fill mx-1">
          <Card.Body>
            <Card.Title>Readability</Card.Title>
            <h2>{scoreData?.data?.readabilityScore || '0'}</h2>
            <ProgressBar now={parseInt(scoreData?.data?.readabilityScore || 0)} />
          </Card.Body>
        </Card>
        <Card className="flex-fill mx-1">
          <Card.Body>
            <Card.Title>Keyword Match</Card.Title>
            <h2>{scoreData?.data?.keywordScore || '0'}</h2>
            <ProgressBar now={parseInt(scoreData?.data?.keywordScore || 0)} />
          </Card.Body>
        </Card>
        <Card className="flex-fill mx-1">
          <Card.Body>
            <Card.Title>Grammar</Card.Title>
            <h2>{scoreData?.data?.grammarScore || '0'}</h2>
            <ProgressBar now={parseInt(scoreData?.data?.grammarScore || 0)} />
          </Card.Body>
        </Card>
      </div>
      <div className="mt-4">
        <h3>Optimization Suggestions:</h3>
        <ul>
          {scoreData?.data?.suggestions?.length > 0
            ? scoreData?.data?.suggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))
            : <li>No optimization suggestions available.</li>}
        </ul>
      </div>
      <div className="mt-4">
        <h4>Readability Suggestions:</h4>
        <ul>
  {scoreData?.data?.suggestions?.readabilitySuggestions ? (
    scoreData?.data?.suggestions?.readabilitySuggestions.split('\n').map((suggestion, index) => (
      <li key={index}>{suggestion}</li>
    ))
  ) : (
    <li>No readability suggestions available.</li>
  )}
</ul>
      </div>
      <div className="mt-4">
        <h4>Grammar Suggestions:</h4>
        <ul>
          {scoreData?.data?.grammarSuggestions?.length > 0
            ? scoreData?.data?.grammarSuggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))
            : <li>No grammar suggestions available.</li>}
        </ul>
      </div>
    </>
  );
  return (
    <div className="py-5">
      <h2 className="text-center">Resume ATS Scorer</h2>
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </div>
  );
};
export default Resumescore;